/* Cart drawer, bento: <m-cart-drawer> (snippets/cart-drawer.liquid, rendered
   by sections/cart-drawer.liquid, the "Cart drawer" section of the overlay
   group, on every page but the cart page). It replaced the
   vendor assets/cart.js on 2026-09-25 (stage 3, step 3.5 of
   docs/_archiwum/plans/ANALIZA-wlasny-motyw-2026-09-25.md); original: git show 464f44e:assets/cart.js.

   Contract kept from the vendor drawer, because other files lean on it:
   - the element name and `open(trigger)` / `close()` (assets/product-card-bento.js,
     <product-form> in assets/product-info-bento.js, assets/header-bento.js);
   - `m-cart-drawer--active` for as long as it is shown, and the backdrop fading
     on the inline `--m-bg-opacity` (assets/header-bento.js dims the mobile bar
     band with it), the panel sliding on `--translate-x`;
   - `prevent-scroll` on <html> while open;
   - `openCartDrawer` on the LeonartoEvents bus once the drawer is in;
   - the `cart:grouped-sections` request: whoever changes the cart dispatches
     it and gets the drawer's section id added to the sections to render
     (assets/gift-wrapping.js, assets/product-info-bento.js,
     assets/product-card-bento.js);
   - every `cartUpdate` on the bus repaints the header, body and footer regions
     from `event.cart.sections[<section id>]`, or fetches the section when the
     payload has none.

   The section id is the group id of the Cart drawer section
   (`sections--…__cart_drawer`), read from the `.shopify-section` wrapper:
   the Section Rendering API renders a group section with its own settings
   only under that id, and with the schema defaults under the file name
   (`cart-drawer`, kept as the fallback for a drawer outside a section).

   In the theme editor, selecting the section opens the drawer and deselecting
   it closes it, so its settings can be seen while they are edited.

   Everything inside is delegated on the element, because each update replaces
   the three regions' innerHTML: quantity changes (the vendor <m-quantity-input>
   from theme-components.js steps the value and fires `change`), Remove, removing a
   discount code, and the order note add-on (the section's "Show order note").

   "You may also like" (`[data-cart-recs]`, in the body region): after each
   repaint and on open, `loadRecs` fills it from the product recommendations
   endpoint — one request per anchor product for the life of the page, the
   answer kept as text — with up to three compact tiles not already in the
   cart. Their size buttons are the product card's quick add, which posts
   and emits `cartUpdate` like any other change.

   New against the vendor drawer: Escape closes it, focus moves to the close
   button on open and back to the trigger on close, Tab and Shift+Tab stay
   inside the open panel (inside the order note while that is open), and the
   panel is a labelled modal dialog. */
(function () {
  if (customElements.get('m-cart-drawer')) return;

  var FALLBACK_SECTION = 'cart-drawer';
  var REGIONS = ['lt-cart-drawer-header', 'lt-cart-drawer-body', 'lt-cart-drawer-footer'];
  var RECS_SHOWN = 3;
  var recsCache = {};

  /* Complementary products first (set up in Search & Discovery), related as
     the fallback, as on the product page (assets/main-product-bento.js). */
  function fetchRecs(url) {
    function get(intent) {
      return fetch(url + '&intent=' + intent)
        .then(function (response) {
          return response.ok ? response.text() : '';
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var result = doc.querySelector('[data-pdp-recs-result]');
          return result && Number(result.getAttribute('data-count')) > 0 ? html : '';
        });
    }
    if (!recsCache[url]) {
      recsCache[url] = get('complementary')
        .then(function (html) {
          return html || get('related');
        })
        .catch(function () {
          delete recsCache[url];
          return '';
        });
    }
    return recsCache[url];
  }
  // The panel's transform transition (assets/cart.css) — the class stays until it ends.
  var CLOSE_DELAY = 300;
  // Typing a quantity waits this long before the request, like the vendor drawer.
  var QTY_DELAY = 300;
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function bus() {
    return window.LeonartoEvents && window.LeonartoTheme && window.LeonartoTheme.pubSubEvents
      ? { events: window.LeonartoEvents, names: window.LeonartoTheme.pubSubEvents }
      : null;
  }

  function routes() {
    var r = (window.LeonartoSettings && window.LeonartoSettings.routes) || {};
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return {
      root: root,
      change: (r.cart_change_url || root + 'cart/change') + '.js',
      update: (r.cart_update_url || root + 'cart/update') + '.js'
    };
  }

  function sectionsToRender() {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections;
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json();
    });
  }

  /* A cart error comes back as 422 `{ status: 'unprocessable_entity', message,
     description }`; a cart never has a `status`. */
  function errorMessage(response) {
    if (!response) return '';
    if (response.errors) return typeof response.errors === 'string' ? response.errors : String(response.errors);
    if (response.status && response.status !== 200) return response.description || response.message || '';
    return '';
  }

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onGroupedSections = this.onGroupedSections.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
      this.onSectionDeselect = this.onSectionDeselect.bind(this);

      var wrapper = this.closest('.shopify-section');
      this.sectionId =
        wrapper && wrapper.id.indexOf('shopify-section-') === 0
          ? wrapper.id.slice('shopify-section-'.length)
          : FALLBACK_SECTION;

      this.qtyTimers = {};
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('cart:grouped-sections', this.onGroupedSections);
      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:select', this.onSectionSelect);
        document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      }

      document.querySelectorAll('.m-cart-icon-bubble').forEach(function (link) {
        link.setAttribute('role', 'button');
        link.setAttribute('aria-haspopup', 'dialog');
      });

      var b = bus();
      if (b) this.unsubscribe = b.events.subscribe(b.names.cartUpdate, this.onCartUpdate.bind(this));
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('cart:grouped-sections', this.onGroupedSections);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
      document.removeEventListener('keydown', this.onKeydown);
      if (this.unsubscribe) this.unsubscribe();
    }

    get inner() {
      return this.querySelector('.m-cart-drawer__inner');
    }

    get loading() {
      if (!this._loading && window.LeonartoLibs && window.LeonartoLibs.AnimateLoading) {
        this._loading = new window.LeonartoLibs.AnimateLoading(this.inner, { overlay: this.inner });
      }
      return this._loading || { start: function () {}, finish: function () {} };
    }

    /* Open / close ---------------------------------------------------------- */

    open(trigger) {
      // An add from inside the open drawer: it stays as it is, and keeps the
      // trigger that opened it (the button inside is gone after the repaint).
      if (this.classList.contains('m-cart-drawer--active') && trigger && this.contains(trigger)) return;
      if (trigger) this.trigger = trigger;
      // Reopened while still closing: the pending close must not hide it.
      clearTimeout(this.closeTimer);
      this.classList.add('m-cart-drawer--active');
      document.documentElement.classList.add('prevent-scroll');
      document.addEventListener('keydown', this.onKeydown);
      var self = this;
      // Two frames: the first paints the drawer at its closed position, so the
      // second one's change of the custom properties transitions.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          self.style.setProperty('--m-bg-opacity', '0.5');
          self.style.setProperty('--translate-x', '0');
          var close = self.querySelector('.m-cart-drawer__close');
          if (close) close.focus({ preventScroll: true });
          self.loadRecs();
          var b = bus();
          if (b) b.events.emit(b.names.openCartDrawer);
        });
      });
    }

    close() {
      if (!this.classList.contains('m-cart-drawer--active')) return;
      this.style.setProperty('--m-bg-opacity', '0');
      this.style.setProperty('--translate-x', '100%');
      document.removeEventListener('keydown', this.onKeydown);
      var hadFocus = this.contains(document.activeElement);
      var trigger = this.trigger;
      this.trigger = null;
      var self = this;
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(function () {
        self.classList.remove('m-cart-drawer--active');
        document.documentElement.classList.remove('prevent-scroll');
      }, CLOSE_DELAY);
      if (hadFocus && trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key === 'Tab') this.trapFocus(event);
    }

    /* The drawer is a modal dialog: the page under the backdrop must not take
       focus (WCAG 2.4.3). The list is read on every Tab, because each cart
       update replaces the regions' markup. The order note panel stays in the
       DOM when closed, only moved out of view (assets/cart.css), so its
       fields are skipped then — and while it is open, focus stays in it. */
    trapFocus(event) {
      var inner = this.inner;
      if (!inner) return;
      var scope = inner.querySelector('.m-cart-addon__body.open') || inner;
      var focusable = Array.prototype.filter.call(scope.querySelectorAll(FOCUSABLE), function (el) {
        return el.getClientRects().length > 0 && !el.closest('.m-cart-addon__body:not(.open)');
      });
      var active = document.activeElement;
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (!scope.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onDocumentClick(event) {
      var link = event.target.closest && event.target.closest('.m-cart-icon-bubble');
      if (!link || !(window.LeonartoSettings && window.LeonartoSettings.enable_cart_drawer)) return;
      event.preventDefault();
      this.open(link);
    }

    onGroupedSections(event) {
      if (event.detail && Array.isArray(event.detail.sections) && event.detail.sections.indexOf(this.sectionId) === -1) {
        event.detail.sections.push(this.sectionId);
      }
    }

    onSectionSelect(event) {
      if (event.target && event.target.contains(this)) this.open();
    }

    onSectionDeselect(event) {
      if (event.target && event.target.contains(this)) this.close();
    }

    /* Clicks inside ---------------------------------------------------------- */

    onClick(event) {
      var target = event.target;
      // The backdrop is the element itself, outside the panel. The close
      // icon's children have `pointer-events: none` (cart.css), so the button
      // is the target.
      if (!target.closest('.m-cart-drawer__inner') || target.closest('.m-cart-drawer__close')) {
        this.close();
        return;
      }

      var remove = target.closest('[data-cart-remove]');
      if (remove) {
        event.preventDefault();
        this.updateQuantity(remove.dataset.index, 0);
        return;
      }

      var discount = target.closest('[data-cart-discount-remove]');
      if (discount) {
        event.preventDefault();
        this.removeDiscount(discount);
        return;
      }

      var addonOpen = target.closest('.m-cart-addon--trigger-button');
      if (addonOpen) {
        event.preventDefault();
        this.openAddon(addonOpen);
        return;
      }

      var addonSave = target.closest('.m-cart-addon--save');
      if (addonSave) {
        event.preventDefault();
        if (addonSave.dataset.action === 'note') this.saveNote();
        this.closeAddon();
        return;
      }

      if (target.closest('.m-cart-addon--close')) {
        event.preventDefault();
        this.closeAddon();
      }
    }

    onChange(event) {
      var input = event.target;
      if (!input.matches('m-quantity-input .m-quantity__input')) return;
      var line = input.dataset.index;
      var self = this;
      clearTimeout(this.qtyTimers[line]);
      this.qtyTimers[line] = setTimeout(function () {
        delete self.qtyTimers[line];
        self.updateQuantity(line, input.value, input);
      }, QTY_DELAY);
    }

    /* Cart requests ---------------------------------------------------------- */

    updateQuantity(line, quantity, input) {
      var self = this;
      this.loading.start();
      postJSON(routes().change, { line: line, quantity: quantity, sections: sectionsToRender() })
        .then(function (response) {
          var message = errorMessage(response);
          if (message) {
            self.showLineError(line, message, input);
            return;
          }
          self.emitUpdate(response);
        })
        .catch(function (error) {
          console.error(error);
        })
        .finally(function () {
          self.loading.finish();
        });
    }

    showLineError(line, message, input) {
      var quantity = input && input.closest('m-quantity-input');
      if (quantity && typeof quantity.setValidity === 'function') quantity.setValidity(message);
      var item = document.getElementById('lt-cart-item-' + line);
      var theme = window.LeonartoTheme;
      if (item && theme && theme.Notification) {
        theme.Notification.show({ target: item, type: 'warning', message: message });
      }
    }

    removeDiscount(button) {
      if (button.getAttribute('aria-disabled') === 'true') return;
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('m-spinner-loading');

      var item = button.closest('li');
      var list = button.closest('.m-cart__discount');
      var codes = [];
      if (list) {
        list.querySelectorAll('.m-cart__discount--item').forEach(function (entry) {
          if (entry !== item) codes.push(entry.dataset.discountCode);
        });
      }

      var body = new FormData();
      body.append('sections', sectionsToRender());
      body.append('sections_url', window.location.pathname);
      body.append('discount', codes.join(','));

      var self = this;
      // /cart/update.js answers with the whole cart, `sections` included.
      fetch(routes().update, { method: 'POST', body: body, headers: { Accept: 'application/json' } })
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          self.emitUpdate(cart);
        })
        .catch(function (error) {
          console.error(error);
          button.removeAttribute('aria-disabled');
          button.classList.remove('m-spinner-loading');
        });
    }

    emitUpdate(cart) {
      var b = bus();
      if (b) b.events.emit(b.names.cartUpdate, { cart: cart });
      else this.render(cart.sections && cart.sections[this.sectionId]);
    }

    /* Repaint ---------------------------------------------------------------- */

    onCartUpdate(event) {
      var cart = event && event.cart;
      var html = cart && cart.sections && cart.sections[this.sectionId];
      if (html) {
        this.render(html);
        return;
      }
      var self = this;
      var id = this.sectionId;
      fetch(routes().root + '?sections=' + encodeURIComponent(id))
        .then(function (response) {
          return response.json();
        })
        .then(function (sections) {
          self.render(sections[id]);
        })
        .catch(function (error) {
          console.error(error);
        });
    }

    render(html) {
      if (!html) return;
      var hadFocus = this.contains(document.activeElement);
      var doc = new DOMParser().parseFromString(html, 'text/html');
      REGIONS.forEach(function (id) {
        var current = document.getElementById(id);
        var next = doc.getElementById(id);
        if (current && next) current.innerHTML = next.innerHTML;
      });
      // The focused control was in a replaced region: back into the panel,
      // not onto <body> behind the modal.
      if (hadFocus && !this.contains(document.activeElement) && this.classList.contains('m-cart-drawer--active')) {
        var close = this.querySelector('.m-cart-drawer__close');
        if (close) close.focus({ preventScroll: true });
      }
      this.loadRecs();
    }

    /* Recommendations ------------------------------------------------------ */

    loadRecs() {
      var box = this.querySelector('[data-cart-recs]');
      if (!box || box.dataset.cartRecsState || typeof DOMParser !== 'function') return;
      box.dataset.cartRecsState = 'loading';
      var exclude = (box.getAttribute('data-exclude') || '').split(',');
      var self = this;
      fetchRecs(box.getAttribute('data-url')).then(function (html) {
        // A repaint may have replaced the box meanwhile; fill the live one.
        var live = self.querySelector('[data-cart-recs]');
        if (live !== box) return;
        box.dataset.cartRecsState = 'done';
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var list = box.querySelector('[data-cart-recs-list]');
        var shown = 0;
        Array.prototype.forEach.call(doc.querySelectorAll('[data-pdp-recs-list="compact"] [data-product-id]'), function (item) {
          if (shown >= RECS_SHOWN || exclude.indexOf(item.getAttribute('data-product-id')) !== -1) return;
          item.className = 'm-cart-drawer__recs-item';
          item.setAttribute('role', 'listitem');
          list.appendChild(document.importNode(item, true));
          shown++;
        });
        box.hidden = shown === 0;
      });
    }

    /* Order note add-on -------------------------------------------------------- */

    openAddon(button) {
      var addon = this.querySelector('#m-addons-' + button.dataset.open);
      this.querySelectorAll('.m-cart-addon--trigger-button.active').forEach(function (active) {
        active.classList.remove('active');
      });
      button.classList.add('active');
      if (addon) addon.classList.add('open');
      var overlay = this.querySelector('.m-cart__overlay');
      if (overlay) overlay.classList.add('open');
    }

    closeAddon() {
      this.querySelectorAll('.m-cart-addon__body.open, .m-cart__overlay.open').forEach(function (node) {
        node.classList.remove('open');
      });
      this.querySelectorAll('.m-cart-addon--trigger-button.active').forEach(function (active) {
        active.classList.remove('active');
      });
    }

    saveNote() {
      var note = this.querySelector('[name="note"]');
      if (!note) return;
      postJSON(routes().update, { note: note.value }).catch(function (error) {
        console.error(error);
      });
    }
  }

  customElements.define('m-cart-drawer', CartDrawer);
})();
